import { useEffect, useState, type RefObject } from 'react';
import type { GuidanceSectionId } from '../lib/newPracticePageGuidance';

export type BlockingSectionViewportDirection = 'up' | 'down' | null;

/**
 * Whether the first blocking guidance section is mostly above or below the
 * visible viewport (for FAB directional hints). Returns null when in view or
 * when there is no blocking section.
 */
export function useBlockingSectionViewportDirection(
  blockingSectionId: GuidanceSectionId | null,
  personaRef: RefObject<HTMLElement | null>,
  scheduleRef: RefObject<HTMLElement | null>,
): BlockingSectionViewportDirection {
  const [direction, setDirection] = useState<BlockingSectionViewportDirection>(null);

  useEffect(() => {
    if (!blockingSectionId) {
      setDirection(null);
      return;
    }
    const el = blockingSectionId === 'persona' ? personaRef.current : scheduleRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setDirection(null);
      return;
    }

    const topInset = 72;
    const bottomInset = 96;

    const compute = () => {
      const r = el.getBoundingClientRect();
      const vh = typeof window.innerHeight === 'number' ? window.innerHeight : 0;
      if (vh <= 0) {
        setDirection(null);
        return;
      }
      const visibleTop = topInset;
      const visibleBottom = vh - bottomInset;
      const overlaps = r.bottom > visibleTop && r.top < visibleBottom;
      if (overlaps) {
        setDirection(null);
        return;
      }
      setDirection(r.top >= visibleBottom ? 'down' : 'up');
    };

    const io = new IntersectionObserver(
      () => {
        compute();
      },
      {
        root: null,
        threshold: [0, 0.05, 0.25, 0.5, 1],
        rootMargin: `-${topInset}px 0px -${bottomInset}px 0px`,
      },
    );

    io.observe(el);
    compute();
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      io.disconnect();
      window.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, [blockingSectionId, personaRef, scheduleRef]);

  return direction;
}
